import { useState, useCallback, useRef } from "react";
import { Upload, FileText, AlertCircle, CheckCircle, X, Download } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  ScrollArea,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Badge,
  Alert,
  AlertDescription,
} from "@/components/ui";
import {
  useImportFinanceCategoriesMutation,
  useToast
} from "@/hooks";
import {
  ImportFinanceCategoryRequestDTO,
  ImportFinanceCategoryError
} from "@/types";
import { cn } from "@/lib/utils";

type ImportFinanceCategoryModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
};

export function ImportFinanceCategoryModal({
  isOpen,
  onClose,
  onSuccess
}: ImportFinanceCategoryModalProps) {
  const { toast } = useToast();
  const { mutateAsync: importFinanceCategories, isLoading } = useImportFinanceCategoriesMutation();

  const [isDragging, setIsDragging] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<ImportFinanceCategoryRequestDTO[]>([]);
  const [validationErrors, setValidationErrors] = useState<ImportFinanceCategoryError[]>([]);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Função para parser CSV simples
  const parseCSV = (text: string): string[][] => {
    const lines = text.split('\n').filter(line => line.trim());
    const result: string[][] = [];

    for (const line of lines) {
      const values: string[] = [];
      let currentValue = '';
      let inQuotes = false;

      for (let i = 0; i < line.length; i++) {
        const char = line[i];

        if (char === '"') {
          if (inQuotes && line[i + 1] === '"') {
            currentValue += '"';
            i++; // Skip next quote
          } else {
            inQuotes = !inQuotes;
          }
        } else if (char === ',' && !inQuotes) {
          values.push(currentValue.trim());
          currentValue = '';
        } else {
          currentValue += char;
        }
      }

      values.push(currentValue.trim());
      result.push(values);
    }

    return result;
  };

  // Função para validar dados da categoria financeira
  const validateFinanceCategoryData = (data: ImportFinanceCategoryRequestDTO[]): ImportFinanceCategoryError[] => {
    const errors: ImportFinanceCategoryError[] = [];

    data.forEach((category, index) => {
      // Validar campo obrigatório name
      if (!category.nome?.trim()) {
        errors.push({
          row: index + 2, // +2 porque row 1 é header e index começa em 0
          field: 'nome',
          error: 'Campo obrigatório ausente',
          data: category
        });
      }

      // Validar tamanho mínimo
      if (category.nome && category.nome.trim().length < 3) {
        errors.push({
          row: index + 2,
          field: 'nome',
          error: 'Nome deve ter no mínimo 3 caracteres',
          data: category
        });
      }

      // Validar tamanho máximo
      if (category.nome && category.nome.trim().length > 50) {
        errors.push({
          row: index + 2,
          field: 'nome',
          error: 'Nome deve ter no máximo 50 caracteres',
          data: category
        });
      }
    });

    return errors;
  };

  // Função para converter CSV para objetos
  const csvToFinanceCategoryData = (csvData: string[][]): ImportFinanceCategoryRequestDTO[] => {
    return csvData.slice(1).map(row => {
      const category: ImportFinanceCategoryRequestDTO = {
        nome: ''
      };

      // Mapeamento de colunas para o campo único
      const header = csvData[0][0]?.toLowerCase() || '';
      const value = row[0] || '';

      if (header === 'nome') {
        category.nome = value;
      } else {
        // Se header não reconhecido, assume que é o campo name
        category.nome = value;
      }

      return category;
    });
  };

  // Processar arquivo
  const processFile = useCallback(async (selectedFile: File) => {
    if (!selectedFile) return;

    if (!selectedFile.name.endsWith('.csv')) {
      toast({
        title: "Erro",
        description: "Por favor, selecione um arquivo CSV",
        variant: "destructive",
      });
      return;
    }

    setFile(selectedFile);

    try {
      const text = await selectedFile.text();
      const csvData = parseCSV(text);

      if (csvData.length < 2) {
        toast({
          title: "Erro",
          description: "Arquivo CSV inválido ou vazio",
          variant: "destructive",
        });
        return;
      }

      const categoryData = csvToFinanceCategoryData(csvData);
      const errors = validateFinanceCategoryData(categoryData);

      setParsedData(categoryData);
      setValidationErrors(errors);
      setIsPreviewMode(true);

    } catch {
      toast({
        title: "Erro",
        description: "Falha ao processar arquivo CSV",
        variant: "destructive",
      });
    }
  }, [toast]);

  // Handlers de drag & drop
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      processFile(files[0]);
    }
  }, [processFile]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      processFile(files[0]);
    }
  };

  // Importar categorias financeiras
  const handleImport = async () => {
    if (!file) return;

    const formData = new FormData();
    formData.append("csv_finance_categorie_entries_registration", file);

    try {
      await importFinanceCategories(formData, {
        onSuccess: () => {
          toast({
            title: "Sucesso",
            description: "Categorias financeiras importadas com sucesso",
          });
          handleClose();
          onSuccess?.();
        },
        onError: (error: unknown) => {
          const errorMessage = error instanceof Error
            ? error.message
            : (error as { response?: { data?: { message?: string } } })?.response?.data?.message || "Falha ao importar categorias financeiras"

          toast({
            title: "Erro",
            description: errorMessage,
            variant: "destructive",
          });
        },
      });
    } catch (error) {
      console.error('Import error:', error);
    }
  };

  // Fechar modal
  const handleClose = () => {
    setFile(null);
    setParsedData([]);
    setValidationErrors([]);
    setIsPreviewMode(false);
    setIsDragging(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  // Download template CSV
  const downloadTemplate = () => {
    const csvContent = `nome
"Receitas de Consultas"
"Despesas com Aluguel"
"Investimentos em Equipamentos"
"Material de Consumo"
"Pagamentos a Fornecedores"`;

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'categorias_financeiras_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-teal-700 font-bold text-xl">
            Importar Categorias Financeiras via CSV
          </DialogTitle>
          <DialogDescription>
            {isPreviewMode
              ? "Revise os dados antes de importar"
              : "Arraste um arquivo CSV ou clique para selecionar"
            }
          </DialogDescription>
        </DialogHeader>

        {!isPreviewMode ? (
          <div className="flex-1 flex flex-col space-y-4">
            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                onClick={downloadTemplate}
                className="text-sm"
              >
                <Download className="w-4 h-4 mr-2" />
                Baixar Template
              </Button>
            </div>

            <div
              className={cn(
                "border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors",
                isDragging
                  ? "border-teal-500 bg-teal-50"
                  : "border-gray-300 hover:border-teal-400 hover:bg-gray-50"
              )}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                className="hidden"
              />

              <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <p className="text-lg font-medium text-gray-700 mb-2">
                {isDragging ? "Solte o arquivo aqui" : "Arraste e solte o arquivo CSV aqui"}
              </p>
              <p className="text-sm text-gray-500 mb-4">
                ou clique para selecionar o arquivo
              </p>

              {file && (
                <div className="flex items-center justify-center space-x-2 text-sm text-teal-600">
                  <FileText className="w-4 h-4" />
                  <span>{file.name}</span>
                </div>
              )}
            </div>

            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Campo obrigatório: <strong>nome</strong> (3-50 caracteres).
                O arquivo deve seguir o formato CSV com cabeçalho na primeira linha.
              </AlertDescription>
            </Alert>
          </div>
        ) : (
          <div className="flex-1 flex flex-col space-y-4">
            {validationErrors.length > 0 && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Foram encontrados {validationErrors.length} erros de validação.
                  Verifique a tabela abaixo antes de importar.
                </AlertDescription>
              </Alert>
            )}

            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">
                {parsedData.length} categorias financeiras encontradas
              </span>
              {validationErrors.length > 0 && (
                <Badge variant="destructive">
                  {validationErrors.length} erros
                </Badge>
              )}
            </div>

            <ScrollArea className="flex-1 border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">Linha</TableHead>
                    <TableHead>Nome</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {parsedData.map((category, index) => {
                    const rowErrors = validationErrors.filter(e => e.row === index + 2);
                    return (
                      <TableRow key={index} className={rowErrors.length > 0 ? "bg-red-50" : ""}>
                        <TableCell className="font-mono text-xs">{index + 2}</TableCell>
                        <TableCell>{category.nome}</TableCell>
                        <TableCell>
                          {rowErrors.length > 0 ? (
                            <Badge variant="destructive" className="text-xs">
                              <X className="w-3 h-3 mr-1" />
                              Erro
                            </Badge>
                          ) : (
                            <Badge variant="default" className="bg-green-500 text-xs">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Válido
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </ScrollArea>

            {validationErrors.length > 0 && (
              <div className="border rounded-lg p-4 bg-gray-50">
                <h4 className="font-medium text-sm mb-2">Erros encontrados:</h4>
                <ScrollArea className="h-24">
                  <ul className="text-xs space-y-1">
                    {validationErrors.map((error, index) => (
                      <li key={index} className="text-red-600">
                        Linha {error.row}: {error.error}
                      </li>
                    ))}
                  </ul>
                </ScrollArea>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="pt-4">
          <Button
            variant="outline"
            onClick={isPreviewMode ? () => setIsPreviewMode(false) : handleClose}
          >
            {isPreviewMode ? "Voltar" : "Cancelar"}
          </Button>

          {isPreviewMode && validationErrors.length === 0 && (
            <Button
              onClick={handleImport}
              isLoading={isLoading}
              className="bg-teal-700 hover:bg-teal-800"
            >
              Importar {parsedData.length} Categorias
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}